// @ts-nocheck
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSidebar } from '../../contexts/SidebarContext';
import { useUser } from '@clerk/clerk-react';
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Search,
  MapPin,
  Target,
  TrendingUp,
  TrendingDown,
  Minus,
  Plus,
  Users,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Loader2,
  Trash2,
  Settings,
} from "lucide-react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { GoogleMap, useJsApiLoader, OverlayView } from "@react-google-maps/api";
import { useGooglePlaces } from '../../hooks/use-google-places';
import { API_CLIENTS, API_KEY } from '../../utils/constants';

const SEO_AUDIT_API = process.env.REACT_APP_SEO_AUDIT_API || 'https://iw0nmalze1.execute-api.us-east-2.amazonaws.com/dev';
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GCP_API_KEY || '';

// Default geo-grid settings (backend stores radius in km)
const DEFAULT_GRID_SIZE = 5;
const DEFAULT_RADIUS_KM = 1;
const KM_PER_MILE = 1.609344;

const milesToKm = (miles: number): number => miles * KM_PER_MILE;
const kmToMiles = (km: number): number => km / KM_PER_MILE;

// Must be a stable reference to avoid infinite re-renders with useJsApiLoader
const GMAP_LIBRARIES: ("places")[] = ["places"];

// Reconstruct lat/lng from grid row/col (same math as backend generateGridCoordinates)
function getGridPointLatLng(
  centerLat: number,
  centerLng: number,
  row: number,
  col: number,
  gridSize: number,
  radiusKm: number
): { lat: number; lng: number } {
  const latPerKm = 1 / 111.32;
  const lngPerKm = 1 / (111.32 * Math.cos(centerLat * (Math.PI / 180)));
  const halfGrid = Math.floor(gridSize / 2);
  const stepKm = radiusKm / (halfGrid || 1);
  return {
    lat: centerLat + (row - halfGrid) * stepKm * latPerKm,
    lng: centerLng + (col - halfGrid) * stepKm * lngPerKm,
  };
}

// ============================================================================
// Types
// ============================================================================
interface TrackedLocation {
  locationId: string;
  locationName: string;
  placeId: string;
  lat: number;
  lng: number;
  address: string;
  competitors?: Array<{ placeId: string; locationName: string }>;
}

interface TrackedKeyword {
  keywordId: string;
  locationId: string;
  keyword: string;
  searchVolume: number | null;
  difficulty: string | null;
  isTracked: boolean;
  gridSize?: number;
  radiusKm?: number;
  createdAt: string;
  // Computed from snapshots
  latestRank?: number | null;
  previousRank?: number | null;
  change?: number;
}

interface RankSnapshot {
  keywordId: string;
  checkDate: string;
  rank: number;
  topCompetitors?: Array<{
    title: string;
    place_id: string;
    rank: number;
    rating?: number;
    reviews?: number;
    address?: string;
    category?: string;
    domain?: string;
  }>;
}

interface GeoGridCell {
  row: number;
  col: number;
  rank: number;
  topCompetitors?: any[];
}

interface GeoGridScan {
  scanDate: string;
  gridSize: number;
  points: GeoGridCell[];
}

interface Competitor {
  title: string;
  place_id: string;
  avgRank: number;
  appearances: number;
  rating?: number;
  reviews?: number;
}

// ============================================================================
// Component
// ============================================================================
const LocalRankTracker = () => {
  const dispatch = useDispatch();
  const { user } = useUser();
  const { sidebarOpen } = useSidebar();
  const auth = useSelector((state: any) => state.auth);
  const selectedClientData = useSelector((state: any) => state.selectedClient);
  const selectedClientId = useSelector((state: any) => state.selectedClient?.selectedClient?.id);
  const [latestClient, setLatestClient] = useState<any>(selectedClientData?.selectedClient || null);
  const mappedGoogleLocationName =
    latestClient?.socialnetworks?.googleMyBusinessLocationName ||
    selectedClientData?.selectedClient?.socialnetworks?.googleMyBusinessLocationName || '';
  const idToken = auth?.userInfo?.accessToken;
  const clientIdentity = selectedClientId || '';
  const userEmail = user?.primaryEmailAddress?.emailAddress || '';
  const isSuperAdmin =
    userEmail.endsWith('@adlerlocal.com') ||
    userEmail.endsWith('@samxlabs.com') ||
    userEmail.endsWith('@adlersocial.com');

  // --- State ---
  const [activeTab, setActiveTab] = useState("geo-grid");
  const [locations, setLocations] = useState<TrackedLocation[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<string>("");
  const [keywords, setKeywords] = useState<TrackedKeyword[]>([]);
  const [geoGridScans, setGeoGridScans] = useState<GeoGridScan[]>([]);
  const [selectedGeoKeywordId, setSelectedGeoKeywordId] = useState<string>("");
  const [selectedScanDate, setSelectedScanDate] = useState<string>("");

  const [newKeyword, setNewKeyword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isAddingKeyword, setIsAddingKeyword] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [seedKeyword, setSeedKeyword] = useState("");
  const [suggestedKeywords, setSuggestedKeywords] = useState<any[]>([]);
  const [isResearching, setIsResearching] = useState(false);
  const [addedSuggestions, setAddedSuggestions] = useState<Set<string>>(new Set());

  // Settings Dialog State
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [editingKeyword, setEditingKeyword] = useState<TrackedKeyword | null>(null);
  const [editGridSize, setEditGridSize] = useState<number>(DEFAULT_GRID_SIZE);
  const [editRadiusMiles, setEditRadiusMiles] = useState<number>(kmToMiles(DEFAULT_RADIUS_KM));
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  // Add Business State
  const [isAddingLocation, setIsAddingLocation] = useState(false);
  const [locationSearchQuery, setLocationSearchQuery] = useState("");
  const [isSavingLocation, setIsSavingLocation] = useState(false);
  const {
    predictions: businessPredictions,
    loading: businessSearchLoading,
    getPlaceDetails: getBusinessDetails,
    clearPredictions: clearBusinessPredictions,
    searchPlaces: searchBusinessPlaces,
    placeDetails: businessPlaceDetails
  } = useGooglePlaces();

  // Add Competitor State
  const [isAddCompetitorOpen, setIsAddCompetitorOpen] = useState(false);
  const [newCompetitorName, setNewCompetitorName] = useState("");
  const [newCompetitorPlaceId, setNewCompetitorPlaceId] = useState("");
  const [isAddingCompetitor, setIsAddingCompetitor] = useState(false);
  // Competitor search autocomplete
  const [competitorSearchQuery, setCompetitorSearchQuery] = useState("");
  const {
    predictions: competitorPredictions,
    loading: competitorSearchLoading,
    getPlaceDetails: getCompetitorDetails,
    clearPredictions: clearCompetitorPredictions,
    searchPlaces: searchCompetitorPlaces
  } = useGooglePlaces();

  const headers = useMemo(
    () => {
      const base: Record<string, string> = {};
      if (idToken) base.Authorization = `Bearer ${idToken}`;
      if (clientIdentity) base['X-Client-Id'] = clientIdentity;
      if (mappedGoogleLocationName) base['X-Google-Location-Name'] = String(mappedGoogleLocationName);
      return base;
    },
    [idToken, clientIdentity, mappedGoogleLocationName]
  );

  // --- Google Maps JS API loader (include 'places' for competitor autocomplete) ---
  const { isLoaded: isMapLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GMAP_LIBRARIES,
  });

  // --- Grid point popup state ---
  const [selectedGridPoint, setSelectedGridPoint] = useState<(GeoGridCell & { lat: number; lng: number }) | null>(null);
  const [clickedGridPoint, setClickedGridPoint] = useState<(GeoGridCell & { lat: number; lng: number }) | null>(null);
  const mapRef = useRef<any>(null);

  // --- Shared keyword loader (with snapshot enrichment) ---
  const loadKeywordsWithSnapshots = useCallback(async (locationId: string) => {
    const res = await axios.get(`${SEO_AUDIT_API}/rank-tracker/keywords/${locationId}`, { headers });
    const kws: TrackedKeyword[] = res.data.keywords || [];

    const enrichedKeywords = await Promise.all(
      kws.map(async (kw) => {
        try {
          const snapRes = await axios.get(
            `${SEO_AUDIT_API}/rank-tracker/snapshots/${kw.keywordId}`,
            {
              headers,
              params: { locationId }
            }
          );
          const snapshots: RankSnapshot[] = snapRes.data.snapshots || [];
          const latest = snapshots[0];
          const previous = snapshots[1];

          return {
            ...kw,
            latestRank: latest?.rank ?? null,
            previousRank: previous?.rank ?? null,
            change: latest && previous ? previous.rank - latest.rank : 0,
          };
        } catch {
          return { ...kw, latestRank: null, previousRank: null, change: 0 };
        }
      })
    );

    setKeywords(enrichedKeywords);

    // Keep currently selected keyword if still present; otherwise pick first.
    setSelectedGeoKeywordId((prev) => {
      if (prev && enrichedKeywords.some((kw) => kw.keywordId === prev)) return prev;
      return enrichedKeywords[0]?.keywordId || '';
    });

    return enrichedKeywords;
  }, [headers]);



  // --- Reset all data when client changes ---
  useEffect(() => {
    setSelectedLocationId("");
    setSelectedGeoKeywordId("");
    setKeywords([]);
    setGeoGridScans([]);
    setSelectedScanDate("");
  }, [clientIdentity]);

  // Refresh the selected client from Social Manager so Rank Tracker always uses the latest
  // connected Google name and listing metadata instead of stale Redux state.
  useEffect(() => {
    if (!clientIdentity || !API_CLIENTS || !API_KEY) return;

    let cancelled = false;

    const loadLatestClient = async () => {
      try {
        const clientRes = await axios.get(`${API_CLIENTS}/${clientIdentity}`, {
          headers: { 'X-API-KEY': API_KEY }
        });

        const maybeClient = clientRes?.data?.data || clientRes?.data?.client || clientRes?.data || null;
        if (!cancelled && maybeClient) {
          setLatestClient(maybeClient);
        }
      } catch (error) {
        console.warn('[RankTracker] Could not refresh latest client from Social Manager:', error);
      }
    };

    loadLatestClient();

    return () => {
      cancelled = true;
    };
  }, [clientIdentity]);

  // --- Fetch locations when client changes ---
  useEffect(() => {
    if (!idToken || !clientIdentity) return;
    const fetchLocations = async () => {
      try {
        const res = await axios.get(`${SEO_AUDIT_API}/rank-tracker/locations`, { headers });
        const locs = res.data.locations || [];
        setLocations(locs);
        // Always set to first location when loading (client just changed)
        if (locs.length > 0) {
          setSelectedLocationId(locs[0].locationId);
        }
      } catch (err) {
        console.error('[RankTracker] Failed to fetch locations:', err);
      }
    };
    fetchLocations();
  }, [idToken, clientIdentity, headers]);

  // --- Fetch keywords when location changes ---
  useEffect(() => {
    if (!idToken || !selectedLocationId) return;
    const fetchKeywords = async () => {
      setIsLoading(true);
      try {
        await loadKeywordsWithSnapshots(selectedLocationId);
      } catch (err) {
        console.error('[RankTracker] Failed to fetch keywords:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchKeywords();
  }, [idToken, selectedLocationId, loadKeywordsWithSnapshots]);

  // --- Fetch geo-grid when keyword changes ---
  useEffect(() => {
    if (!idToken || !selectedLocationId || !selectedGeoKeywordId) return;
    const fetchGeoGrid = async () => {
      try {
        const res = await axios.get(
          `${SEO_AUDIT_API}/rank-tracker/geo-grids/${selectedLocationId}/${selectedGeoKeywordId}`,
          { headers }
        );
        setGeoGridScans(res.data.scans || []);
      } catch (err) {
        console.error('[RankTracker] Failed to fetch geo-grid:', err);
      }
    };
    fetchGeoGrid();
  }, [idToken, selectedLocationId, selectedGeoKeywordId]);

  // --- Auto-select latest scan date when geoGridScans change ---
  useEffect(() => {
    if (geoGridScans && geoGridScans.length > 0) {
      if (!selectedScanDate || !geoGridScans.find(s => s.scanDate === selectedScanDate)) {
        setSelectedScanDate(geoGridScans[0].scanDate);
        setClickedGridPoint(null);
        setSelectedGridPoint(null);
      }
    } else {
      setSelectedScanDate("");
    }
  }, [geoGridScans, selectedScanDate]);

  const displayedGeoGrid = useMemo(() => {
    if (!geoGridScans || geoGridScans.length === 0) return null;
    if (!selectedScanDate) return geoGridScans[0];
    return geoGridScans.find(s => s.scanDate === selectedScanDate) || geoGridScans[0];
  }, [geoGridScans, selectedScanDate]);

  // --- Build competitor data from geo-grid scans (no extra API calls needed) ---
  // This runs whenever displayedGeoGrid changes, aggregating all topCompetitors across all grid points
  const computedCompetitors = useMemo<Competitor[]>(() => {
    if (!displayedGeoGrid) return [];

    const competitorMap = new Map<string, { title: string; ranks: number[]; rating?: number; reviews?: number }>();

    for (const point of displayedGeoGrid.points) {
      const topComps = point.topCompetitors || [];
      for (const comp of topComps) {
        if (!comp.place_id) continue;
        const existing = competitorMap.get(comp.place_id);
        if (existing) {
          existing.ranks.push(comp.rank);
          // Update rating/reviews if present
          if (comp.rating) existing.rating = comp.rating;
          if (comp.reviews) existing.reviews = comp.reviews;
        } else {
          competitorMap.set(comp.place_id, {
            title: comp.title || 'Unknown',
            ranks: [comp.rank],
            rating: comp.rating,
            reviews: comp.reviews,
          });
        }
      }
    }

    const compArray: Competitor[] = Array.from(competitorMap.entries()).map(([place_id, data]) => ({
      title: data.title,
      place_id,
      avgRank: parseFloat((data.ranks.reduce((a, b) => a + b, 0) / data.ranks.length).toFixed(1)),
      appearances: data.ranks.length,
      rating: data.rating,
      reviews: data.reviews,
    }));
    compArray.sort((a, b) => a.avgRank - b.avgRank);
    return compArray.slice(0, 20);
  }, [geoGridScans]);

  // --- Add keyword ---
  const handleAddKeyword = async () => {
    if (!newKeyword.trim() || !selectedLocationId || !idToken) return;
    setIsAddingKeyword(true);
    try {
      await axios.post(`${SEO_AUDIT_API}/rank-tracker/keywords`, {
        locationId: selectedLocationId,
        keyword: newKeyword.trim(),
      }, { headers });
      setNewKeyword("");
      await loadKeywordsWithSnapshots(selectedLocationId);
    } catch (err) {
      console.error('[RankTracker] Failed to add keyword:', err);
      alert('Failed to add keyword');
    } finally {
      setIsAddingKeyword(false);
    }
  };

  // --- Toggle keyword tracking ---
  const handleToggleKeyword = async (kw: TrackedKeyword) => {
    if (!idToken) return;
    try {
      await axios.put(`${SEO_AUDIT_API}/rank-tracker/keywords/toggle`, {
        locationId: kw.locationId,
        keywordId: kw.keywordId,
        isTracked: !kw.isTracked,
      }, { headers });
      setKeywords(keywords.map(k =>
        k.keywordId === kw.keywordId ? { ...k, isTracked: !k.isTracked } : k
      ));
    } catch (err) {
      console.error('[RankTracker] Failed to toggle keyword:', err);
    }
  };

  // --- Delete keyword ---
  const handleDeleteKeyword = async (kw: TrackedKeyword) => {
    if (!idToken) return;
    try {
      await axios.delete(`${SEO_AUDIT_API}/rank-tracker/keywords/${kw.locationId}/${kw.keywordId}`, { headers });
      setKeywords(keywords.filter(k => k.keywordId !== kw.keywordId));
    } catch (err) {
      console.error('[RankTracker] Failed to delete keyword:', err);
    }
  };

  // --- Settings ---
  const handleOpenSettings = (kw: TrackedKeyword) => {
    setEditingKeyword(kw);
    setEditGridSize(kw.gridSize || DEFAULT_GRID_SIZE);
    setEditRadiusMiles(kmToMiles(kw.radiusKm || DEFAULT_RADIUS_KM));
    setIsSettingsOpen(true);
  };

  const handleSaveSettings = async () => {
    if (!editingKeyword || !idToken) return;
    setIsSavingSettings(true);
    try {
      await axios.put(`${SEO_AUDIT_API}/rank-tracker/keywords/settings`, {
        locationId: editingKeyword.locationId,
        keywordId: editingKeyword.keywordId,
        gridSize: editGridSize,
        // API remains in km; convert from miles-based UI.
        radiusKm: milesToKm(editRadiusMiles),
      }, { headers });

      setKeywords(keywords.map(k =>
        k.keywordId === editingKeyword.keywordId
          ? { ...k, gridSize: editGridSize, radiusKm: milesToKm(editRadiusMiles) }
          : k
      ));
      setIsSettingsOpen(false);

      // If the currently selected geo grid keyword is the one we just updated, 
      // its map size might change on next scan. The user has to scan again.

    } catch (err) {
      console.error('[RankTracker] Failed to update settings:', err);
      alert('Failed to save settings. Please try again.');
    } finally {
      setIsSavingSettings(false);
    }
  };

  // --- Competitor Places autocomplete ---
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (competitorSearchQuery.length >= 3) {
        searchCompetitorPlaces(competitorSearchQuery);
      } else {
        clearCompetitorPredictions();
      }
    }, 400);
    return () => clearTimeout(timeout);
  }, [competitorSearchQuery, searchCompetitorPlaces, clearCompetitorPredictions]);

  const handleCompetitorPredictionSelect = async (placeId: string, description: string) => {
    setCompetitorSearchQuery(description);
    clearCompetitorPredictions();
    // Fetch full place details to get the canonical name and place_id
    await getCompetitorDetails(placeId);
    setNewCompetitorPlaceId(placeId);
    // Extract just the business name (before the first comma)
    const namePart = description.split(',')[0].trim();
    setNewCompetitorName(namePart);
  };

  // --- Debounce business search ---
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (locationSearchQuery.length >= 3) {
        searchBusinessPlaces(locationSearchQuery);
      } else {
        clearBusinessPredictions();
      }
    }, 400);
    return () => clearTimeout(timeout);
  }, [locationSearchQuery, searchBusinessPlaces, clearBusinessPredictions]);

  // --- Handle new business selection and save ---
  useEffect(() => {
    const saveNewBusiness = async () => {
      if (!businessPlaceDetails || !idToken || !clientIdentity) return;
      setIsSavingLocation(true);
      try {
        const payload = {
          locationName: businessPlaceDetails.name,
          address: businessPlaceDetails.formatted_address,
          placeId: businessPlaceDetails.place_id,
          lat: businessPlaceDetails.geometry.location.lat,
          lng: businessPlaceDetails.geometry.location.lng,
        };

        const res = await axios.post(`${SEO_AUDIT_API}/rank-tracker/locations`, payload, { headers });
        const newLoc = res.data.location;

        setLocations(prev => {
          if (prev.some(l => l.locationId === newLoc.locationId)) return prev;
          return [...prev, newLoc];
        });
        setSelectedLocationId(newLoc.locationId);
        setIsAddingLocation(false);
        setLocationSearchQuery("");
        clearBusinessPredictions();
      } catch (err) {
        console.error('[RankTracker] Failed to save business:', err);
        alert('Failed to save business profile. Please try again.');
      } finally {
        setIsSavingLocation(false);
      }
    };
    saveNewBusiness();
  }, [businessPlaceDetails, idToken, clientIdentity, headers]);

  // --- Competitors ---
  const handleAddCompetitor = async () => {
    if (!idToken || !selectedLocationId || !newCompetitorName.trim() || !newCompetitorPlaceId.trim()) return;

    setIsAddingCompetitor(true);
    try {
      const res = await axios.post(
        `${SEO_AUDIT_API}/rank-tracker/locations/${selectedLocationId}/competitors`,
        { placeId: newCompetitorPlaceId.trim(), locationName: newCompetitorName.trim() },
        { headers }
      );

      const updatedLocation = res.data.location;
      setLocations(locations.map(loc => loc.locationId === selectedLocationId ? updatedLocation : loc));
      setIsAddCompetitorOpen(false);
      setNewCompetitorName("");
      setNewCompetitorPlaceId("");
      setCompetitorSearchQuery("");
      clearCompetitorPredictions();
    } catch (err) {
      console.error('[RankTracker] Failed to add competitor:', err);
      alert('Failed to add competitor. Please try again.');
    } finally {
      setIsAddingCompetitor(false);
    }
  };

  const handleRemoveCompetitor = async (placeId: string) => {
    if (!idToken || !selectedLocationId || !window.confirm('Are you sure you want to remove this tracked competitor?')) return;

    try {
      const res = await axios.delete(
        `${SEO_AUDIT_API}/rank-tracker/locations/${selectedLocationId}/competitors/${placeId}`,
        { headers }
      );

      const updatedLocation = res.data.location;
      setLocations(locations.map(loc => loc.locationId === selectedLocationId ? updatedLocation : loc));
    } catch (err) {
      console.error('[RankTracker] Failed to remove competitor:', err);
      alert('Failed to remove competitor. Please try again.');
    }
  };

  // --- Manual Scan with Polling ---
  const [scanProgress, setScanProgress] = useState<{ total: number, completed: number, status: string } | null>(null);

  const fetchGeoGridData = useCallback(async (locId: string, kwId: string) => {
    try {
      const res = await axios.get(
        `${SEO_AUDIT_API}/rank-tracker/geo-grids/${locId}/${kwId}`,
        { headers }
      );
      setGeoGridScans(res.data.scans || []);
    } catch (err) {
      console.error('[RankTracker] Failed to refresh geo-grid:', err);
    }
  }, [headers]);

  const pollScanResults = async (locationId: string, keywordId: string, retryCount = 0) => {
    try {
      const res = await axios.post(`${SEO_AUDIT_API}/rank-tracker/keywords/scan/results`, {
        locationId,
        keywordId,
      }, { headers });

      const { status, progress } = res.data;

      if (status === 'completed') {
        setScanProgress(null);
        setIsScanning(false);
        // Silently refresh the geo grid data without alert
        await fetchGeoGridData(locationId, keywordId);
      } else if (status === 'failed') {
        setScanProgress(null);
        setIsScanning(false);
        const failedCount = progress?.failed || 0;
        const totalCount = progress?.total || 0;
        alert(`Scan failed (${failedCount}/${totalCount} tasks failed). Please try again.`);
      } else {
        // Still pending
        setScanProgress({
          total: progress.total,
          completed: progress.completed,
          status: 'pending'
        });
        // Poll again in 10 seconds
        setTimeout(() => pollScanResults(locationId, keywordId, 0), 10000);
      }
    } catch (err) {
      console.error('[RankTracker] Polling failed:', err);

      const axiosErr = err as any;
      const statusCode = axiosErr?.response?.status;
      const apiMessage = axiosErr?.response?.data?.error || axiosErr?.response?.data?.message;

      // 4xx means request/context issues (not transient network interruptions).
      // Show the backend message directly so users can take the right action.
      if (statusCode >= 400 && statusCode < 500) {
        setIsScanning(false);
        setScanProgress(null);
        alert(apiMessage || 'Scan polling failed due to request context. Please refresh and try again.');
        return;
      }

      // Transient network/API gateway interruptions can happen while scan jobs
      // are still running in background. Retry a few times before failing.
      if (retryCount < 4) {
        setTimeout(() => pollScanResults(locationId, keywordId, retryCount + 1), 7000);
        return;
      }

      setIsScanning(false);
      setScanProgress(null);
      alert('Scan polling failed due to network issues. Please click Scan Again to continue.');
    }
  };

  const handleManualScan = async () => {
    if (!selectedGeoKeywordId || !selectedLocationId || !idToken) return;
    setIsScanning(true);
    setScanProgress({ total: 0, completed: 0, status: 'starting' });
    try {
      await axios.post(`${SEO_AUDIT_API}/rank-tracker/keywords/scan`, {
        locationId: selectedLocationId,
        keywordId: selectedGeoKeywordId,
      }, { headers });

      // Start polling after 5 seconds
      setTimeout(() => {
        pollScanResults(selectedLocationId, selectedGeoKeywordId, 0);
      }, 5000);

    } catch (err) {
      console.error('[RankTracker] Failed to manually scan:', err);
      setIsScanning(false);
      setScanProgress(null);
      alert('Failed to trigger scan. Please try again.');
    }
  };

  // --- Keyword Research ---
  const handleResearch = async () => {
    if (!seedKeyword.trim()) return;
    setIsResearching(true);
    setSuggestedKeywords([]);
    setAddedSuggestions(new Set());
    try {
      const locationCoordinate = selectedLocation ? `${selectedLocation.lat},${selectedLocation.lng}` : undefined;
      const res = await axios.post(`${SEO_AUDIT_API}/rank-tracker/keywords/research`, {
        seedKeyword: seedKeyword.trim(),
        locationCoordinate,
        limit: 20,
      }, { headers });
      setSuggestedKeywords(res.data.suggestions || []);
    } catch (err) {
      console.error('[RankTracker] Keyword research failed:', err);
      alert('Failed to fetch keyword suggestions. Please try again.');
    } finally {
      setIsResearching(false);
    }
  };

  // --- Add suggested keyword to tracking ---
  const handleAddSuggestedKeyword = async (suggestion: any) => {
    if (!selectedLocationId || !idToken) return;
    try {
      await axios.post(`${SEO_AUDIT_API}/rank-tracker/keywords`, {
        locationId: selectedLocationId,
        keyword: suggestion.keyword,
        searchVolume: suggestion.searchVolume,
        difficulty: suggestion.keywordDifficulty,
      }, { headers });
      setAddedSuggestions(prev => new Set(prev).add(suggestion.keyword));
      await loadKeywordsWithSnapshots(selectedLocationId);
    } catch (err) {
      console.error('[RankTracker] Failed to add suggested keyword:', err);
      alert('Failed to add keyword.');
    }
  };

  // --- Helpers ---
  const getRankColor = (rank: number | null | undefined) => {
    if (rank === null || rank === undefined || rank >= 101) return "bg-muted text-muted-foreground";
    if (rank <= 3) return "bg-green-500 text-white";
    if (rank < 20) return "bg-amber-500 text-white";
    return "bg-red-500 text-white";
  };

  const getChangeIcon = (change: number | undefined) => {
    if (!change || change === 0) return <span className="flex items-center text-muted-foreground"><Minus className="h-4 w-4" />0</span>;
    if (change > 0) return <span className="flex items-center text-green-600"><ChevronUp className="h-4 w-4" />{change}</span>;
    return <span className="flex items-center text-red-600"><ChevronDown className="h-4 w-4" />{Math.abs(change)}</span>;
  };

  // --- Computed Stats ---
  const trackedKeywords = keywords.filter(k => k.isTracked);
  const rankedKeywords = trackedKeywords.filter(k => k.latestRank && k.latestRank < 101);
  const avgPosition = rankedKeywords.length > 0
    ? parseFloat((rankedKeywords.reduce((sum, k) => sum + (k.latestRank || 0), 0) / rankedKeywords.length).toFixed(1))
    : 0;
  const top3Count = rankedKeywords.filter(k => (k.latestRank || 999) <= 3).length;
  const selectedLocation = locations.find(l => l.locationId === selectedLocationId);
  const selectedGeoKeyword = keywords.find(k => k.keywordId === selectedGeoKeywordId);
  const selectedRadiusKm = selectedGeoKeyword?.radiusKm || DEFAULT_RADIUS_KM;

  const gridSize = displayedGeoGrid ? Math.round(displayedGeoGrid.gridSize) || 5 : 5;

  // --- Compute grid points with real lat/lng coordinates ---
  const gridPointsWithCoords = useMemo(() => {
    if (!displayedGeoGrid || !selectedLocation) return [];
    const gs = gridSize;
    return displayedGeoGrid.points
      .sort((a: GeoGridCell, b: GeoGridCell) => a.row === b.row ? a.col - b.col : a.row - b.row)
      .map((point: GeoGridCell) => {
        const coords = getGridPointLatLng(
          selectedLocation.lat,
          selectedLocation.lng,
          point.row,
          point.col,
          gs,
          selectedRadiusKm
        );
        return { ...point, lat: coords.lat, lng: coords.lng };
      });
  }, [displayedGeoGrid, selectedLocation, gridSize, selectedRadiusKm]);

  // Keep map framed to always show all grid dots (and the business center marker).
  useEffect(() => {
    if (!isMapLoaded || !mapRef.current || !gridPointsWithCoords.length || !window.google?.maps) return;

    const bounds = new window.google.maps.LatLngBounds();

    gridPointsWithCoords.forEach((point) => {
      bounds.extend({ lat: point.lat, lng: point.lng });
    });

    if (selectedLocation) {
      bounds.extend({ lat: selectedLocation.lat, lng: selectedLocation.lng });
    }

    mapRef.current.fitBounds(bounds, 64);
  }, [isMapLoaded, gridPointsWithCoords, selectedLocation, selectedScanDate, selectedGeoKeywordId]);

  return (
    <main className={`p-3 sm:p-4 lg:p-10 ${sidebarOpen ? 'lg:pl-14' : 'lg:pl-10'} space-y-8 bg-muted/30 min-h-screen transition-all duration-200`}>

      {/* Page Title */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-2">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
            Local Rank Tracker,{' '}
            {latestClient
              ? latestClient.clientName || latestClient.name || ''
              : ''}
          </h1>
          <p className="text-base text-muted-foreground mt-1">
            Track your local search rankings and monitor competitors
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          <div className="flex items-center gap-2">
            <div className="w-full sm:w-96 relative">
              {!isAddingLocation ? (
                <Select value={selectedLocationId} onValueChange={setSelectedLocationId}>
                  <SelectTrigger className="h-14 rounded-xl text-base bg-muted/40 min-w-[200px]">
                    <SelectValue placeholder="Select business profile" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.length > 0 ? (
                      locations.map(loc => (
                        <SelectItem key={loc.locationId} value={loc.locationId}>
                          {loc.locationName}
                        </SelectItem>
                      ))
                    ) : (
                      <div className="p-2 text-sm text-muted-foreground">No businesses added</div>
                    )}
                  </SelectContent>
                </Select>
              ) : (
                <div className="relative">
                  <Input
                    className="h-14 rounded-xl text-base pl-10 pr-10"
                    placeholder="Search Google for business..."
                    value={locationSearchQuery}
                    onChange={(e) => setLocationSearchQuery(e.target.value)}
                    autoFocus
                  />
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  {businessSearchLoading && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                  
                  {/* Dropdown Predictions */}
                  {businessPredictions.length > 0 && (
                    <div className="absolute top-full left-0 w-full mt-1 bg-background border rounded-xl shadow-xl z-[100] max-h-60 overflow-y-auto overflow-x-hidden">
                      {businessPredictions.map((p) => (
                        <div
                          key={p.place_id}
                          className="p-3 hover:bg-muted cursor-pointer border-b last:border-0 flex items-start gap-3 transition-colors"
                          onClick={() => {
                            getBusinessDetails(p.place_id);
                            setLocationSearchQuery(p.structured_formatting.main_text);
                            clearBusinessPredictions();
                          }}
                        >
                          <MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{p.structured_formatting.main_text}</p>
                            <p className="text-xs text-muted-foreground truncate">{p.structured_formatting.secondary_text}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <Button
              variant={isAddingLocation ? "outline" : "default"}
              className="h-14 px-6 rounded-xl text-base gap-2"
              onClick={() => {
                setIsAddingLocation(!isAddingLocation);
                setLocationSearchQuery("");
                clearBusinessPredictions();
              }}
              disabled={isSavingLocation}
            >
              {isSavingLocation ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : isAddingLocation ? (
                "Cancel"
              ) : (
                <>
                  <Plus className="h-5 w-5" />
                  Add Business
                </>
              )}
            </Button>
          </div>
          <Button
            variant="outline"
            className="gap-2 h-14 px-6 rounded-xl text-base"
            onClick={() => {
              // Refetch all data
              setSelectedLocationId(prev => {
                // Trigger re-fetch by toggling state
                return prev;
              });
              window.location.reload();
            }}
          >
            <RefreshCw className="h-5 w-5" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="space-y-6">

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <Target className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{avgPosition || '-'}</p>
                <p className="text-sm text-muted-foreground">Avg. Position</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Search className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{trackedKeywords.length}</p>
                <p className="text-sm text-muted-foreground">Tracked Keywords</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <TrendingUp className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{top3Count}</p>
                <p className="text-sm text-muted-foreground">Top 3 Rankings</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Users className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{computedCompetitors.length}</p>
                <p className="text-sm text-muted-foreground">Competitors Found</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="mt-3 w-full justify-start overflow-x-auto whitespace-nowrap">
            <TabsTrigger value="geo-grid" className="whitespace-nowrap">Geo Grid</TabsTrigger>
            <TabsTrigger value="keywords" className="whitespace-nowrap">Keywords</TabsTrigger>
            <TabsTrigger value="research" className="whitespace-nowrap">Keyword Research</TabsTrigger>
            <TabsTrigger value="competitors" className="whitespace-nowrap">Competitors</TabsTrigger>
          </TabsList>

          {/* =============== GEO-GRID TAB =============== */}
          <TabsContent tabValue="geo-grid" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-primary" />
                      Geo Grid Rankings
                    </CardTitle>
                    <CardDescription>
                      See how you rank across different locations in your service area
                    </CardDescription>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full lg:w-auto">
                    {geoGridScans.length > 0 && (
                      <div className="space-y-1">
                        <Label className="text-sm">Scan Date:</Label>
                        <Select value={selectedScanDate} onValueChange={(val) => {
                          setSelectedScanDate(val);
                          setClickedGridPoint(null);
                          setSelectedGridPoint(null);
                        }}>
                          <SelectTrigger className="w-full min-w-0 md:w-60">
                            <SelectValue placeholder="Select date" />
                          </SelectTrigger>
                          <SelectContent>
                            {geoGridScans.map((scan) => (
                              <SelectItem key={scan.scanDate} value={scan.scanDate}>
                                {new Date(scan.scanDate).toLocaleDateString(undefined, {
                                  year: 'numeric', month: 'short', day: 'numeric',
                                  hour: '2-digit', minute: '2-digit'
                                })}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="space-y-1">
                      <Label className="text-sm">Keyword:</Label>
                      <Select value={selectedGeoKeywordId} onValueChange={setSelectedGeoKeywordId}>
                        <SelectTrigger className="w-full min-w-0 md:w-72">
                          <SelectValue placeholder="Select keyword" />
                        </SelectTrigger>
                        <SelectContent>
                          {trackedKeywords.map(kw => (
                            <SelectItem key={kw.keywordId} value={kw.keywordId}>{kw.keyword}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {!displayedGeoGrid ? (
                  <div className="text-center py-16 text-muted-foreground">
                    <MapPin className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p className="text-lg">No geo-grid data yet</p>
                    <p className="text-sm mt-1 mb-4">
                      {selectedLocationId
                        ? 'Geo-grid data will appear after the weekly scan runs, or you can trigger a manual scan.'
                        : 'Data will appear once a business and keywords are configured.'}
                    </p>
                    {!displayedGeoGrid && trackedKeywords.length === 0 ? (
                      <p className="text-sm mt-2">Add keywords first in the Keywords tab.</p>
                    ) : (
                      <Button
                        onClick={handleManualScan}
                        disabled={isScanning || !selectedGeoKeywordId}
                        variant="default"
                        size="lg"
                        className="mt-2"
                      >
                        {isScanning ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            {scanProgress?.status === 'starting'
                              ? 'Queuing Scan...'
                              : `Scanning... ${scanProgress?.completed || 0}/${scanProgress?.total || 26}`}
                          </>
                        ) : (
                          <>
                            <RefreshCw className="mr-2 h-5 w-5" />
                            Scan Now
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="rounded-2xl overflow-hidden border w-full h-[420px] md:h-[520px]">
                      {isMapLoaded ? (
                        <GoogleMap
                          mapContainerStyle={{ width: '100%', height: '100%' }}
                          center={selectedLocation ? { lat: selectedLocation.lat, lng: selectedLocation.lng } : { lat: 25.76, lng: -80.19 }}
                          zoom={gridSize <= 3 ? 14 : gridSize <= 5 ? 13 : 12}
                          onLoad={(map) => {
                            mapRef.current = map;
                          }}
                          onUnmount={() => {
                            mapRef.current = null;
                          }}
                          options={{
                            disableDefaultUI: false,
                            zoomControl: true,
                            mapTypeControl: true,
                            streetViewControl: false,
                            fullscreenControl: true,
                            styles: [
                              { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
                            ],
                          }}
                        >
                          {/* Business center marker */}
                          {selectedLocation && (
                            <OverlayView
                              position={{ lat: selectedLocation.lat, lng: selectedLocation.lng }}
                              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                            >
                              <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 -ml-4 sm:-ml-5 -mt-4 sm:-mt-5 rounded-full bg-blue-600 text-white shadow-xl border-2 border-white animate-pulse">
                                <MapPin className="h-4 w-4 sm:h-5 sm:w-5" />
                              </div>
                            </OverlayView>
                          )}
                          {/* Grid point markers */}
                          {gridPointsWithCoords.map((point, idx) => (
                            <OverlayView
                              key={idx}
                              position={{ lat: point.lat, lng: point.lng }}
                              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                            >
                              {(() => {
                                const isActivePoint =
                                  clickedGridPoint?.row === point.row &&
                                  clickedGridPoint?.col === point.col;
                                return (
                                  <div
                                    className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 -ml-4 sm:-ml-5 md:-ml-6 -mt-4 sm:-mt-5 md:-mt-6 rounded-full ${getRankColor(point.rank)} shadow-lg cursor-pointer transition-transform hover:scale-125 hover:z-50 ${isActivePoint
                                      ? 'border-4 border-primary ring-4 ring-primary/35 scale-110 z-50'
                                      : 'border-2 border-white/80'
                                      }`}
                                    title={`Rank: ${point.rank >= 101 ? '>100' : point.rank}`}
                                    onMouseEnter={() => setSelectedGridPoint(point)}
                                    onMouseLeave={() => {
                                      if (
                                        selectedGridPoint?.row === point.row &&
                                        selectedGridPoint?.col === point.col
                                      ) {
                                        setSelectedGridPoint(null);
                                      }
                                    }}
                                    onClick={() => setClickedGridPoint(clickedGridPoint?.row === point.row && clickedGridPoint?.col === point.col ? null : point)}
                                  >
                                    <span className="text-sm font-bold drop-shadow-sm">
                                      {point.rank >= 101 ? '>100' : point.rank}
                                    </span>
                                  </div>
                                );
                              })()}
                            </OverlayView>
                          ))}
                          {/* Grid Point Info Popup */}
                          {selectedGridPoint && (
                            <OverlayView
                              position={{ lat: selectedGridPoint.lat, lng: selectedGridPoint.lng }}
                              mapPaneName={OverlayView.FLOAT_PANE}
                            >
                              <div className="ml-8 -mt-16 bg-white dark:bg-gray-900 rounded-xl shadow-2xl border p-4 w-72 z-[100]">
                                <div className="flex justify-between items-center mb-2">
                                  <span className="text-xs font-medium text-muted-foreground">Grid [{selectedGridPoint.row},{selectedGridPoint.col}]</span>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setSelectedGridPoint(null); }}
                                    className="text-muted-foreground hover:text-foreground text-lg leading-none"
                                  >×</button>
                                </div>
                                <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-bold mb-3 ${getRankColor(selectedGridPoint.rank)}`}>
                                  {selectedGridPoint.rank >= 101 ? '>100' : `Rank #${selectedGridPoint.rank}`}
                                </div>
                                <div className="mb-3 text-[11px] text-muted-foreground leading-tight">
                                  <p>Lat: {selectedGridPoint.lat.toFixed(6)}</p>
                                  <p>Lng: {selectedGridPoint.lng.toFixed(6)}</p>
                                </div>
                                {selectedGridPoint.topCompetitors && selectedGridPoint.topCompetitors.length > 0 ? (
                                  <div>
                                    <p className="text-xs font-semibold text-muted-foreground mb-1.5">Top Competitors:</p>
                                    {selectedGridPoint.topCompetitors.slice(0, 5).map((comp: any, ci: number) => (
                                      <div key={ci} className="flex items-center gap-2 py-1 border-b last:border-0">
                                        <span className={`w-5 h-5 flex items-center justify-center rounded-full text-[10px] font-bold ${ci === 0 ? 'bg-green-500 text-white' : ci === 1 ? 'bg-amber-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-foreground'}`}>
                                          {comp.rank}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                          <p className="text-xs font-medium truncate">{comp.locationName || comp.title || 'Unknown'}</p>
                                          {comp.rating && <p className="text-[10px] text-muted-foreground">⭐ {comp.rating} ({comp.reviews || 0} reviews)</p>}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-xs text-muted-foreground">No competitor data for this point.</p>
                                )}
                              </div>
                            </OverlayView>
                          )}
                        </GoogleMap>
                      ) : (
                        <div className="flex items-center justify-center h-full bg-muted/50">
                          <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col sm:flex-row items-center justify-between mt-4 mb-2">
                      <p className="text-sm text-muted-foreground">
                        Scan date: {new Date(displayedGeoGrid.scanDate).toLocaleString(undefined, {
                          year: 'numeric', month: 'short', day: 'numeric',
                          hour: 'numeric', minute: 'numeric'
                        })} • {displayedGeoGrid.points.length} grid points
                      </p>
                      <Button
                        onClick={handleManualScan}
                        disabled={isScanning || !selectedGeoKeywordId}
                        variant="outline"
                        size="sm"
                        className="mt-2 sm:mt-0"
                      >
                        {isScanning ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            {scanProgress?.status === 'starting'
                              ? 'Queuing...'
                              : `${scanProgress?.completed || 0}/${scanProgress?.total || 26}`}
                          </>
                        ) : (
                          <>
                            <RefreshCw className="mr-2 h-4 w-4" />
                            Scan Again
                          </>
                        )}
                      </Button>
                    </div>
                    {/* Legend */}
                    <div className="flex justify-center gap-6 mt-4">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-blue-600 border border-white" />
                        <span className="text-sm">Your Business</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-green-500" />
                        <span className="text-sm">Position 1-3</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-amber-500" />
                        <span className="text-sm">Position 4-19</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-red-500" />
                        <span className="text-sm">Position 20-100</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-muted border" />
                        <span className="text-sm">{'>100'}</span>
                      </div>
                    </div>

                    {/* Clicked Grid Point Competitors Table */}
                    {clickedGridPoint && (
                      <div className="mt-8 border-t pt-6 bg-card" id="clicked-grid-point-section">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="text-lg font-semibold flex items-center gap-2">
                              <MapPin className="h-5 w-5 text-primary" />
                              Grid Point [{clickedGridPoint.row}, {clickedGridPoint.col}] Competitors
                            </h3>
                            <p className="text-sm text-muted-foreground mt-1">
                              Your Rank: <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-bold ${getRankColor(clickedGridPoint.rank)}`}>{clickedGridPoint.rank >= 101 ? '>100' : clickedGridPoint.rank}</span>
                            </p>
                            <p className="text-sm text-muted-foreground">
                              Dot Coordinates: {clickedGridPoint.lat.toFixed(6)}, {clickedGridPoint.lng.toFixed(6)}
                            </p>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => setClickedGridPoint(null)}>
                            Close
                          </Button>
                        </div>

                        {!clickedGridPoint.topCompetitors || clickedGridPoint.topCompetitors.length === 0 ? (
                          <div className="text-center py-8 text-muted-foreground bg-muted/20 rounded-lg">
                            <Target className="h-8 w-8 mx-auto mb-2 opacity-30" />
                            <p>No competitor data available for this point.</p>
                          </div>
                        ) : (
                          <div className="rounded-md border overflow-hidden">
                            <Table>
                              <TableHeader className="bg-muted/50">
                                <TableRow>
                                  <TableHead className="w-[80px] text-center">Rank</TableHead>
                                  <TableHead>Competitor</TableHead>
                                  <TableHead className="text-right">Rating</TableHead>
                                  <TableHead className="text-right">Reviews</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {clickedGridPoint.topCompetitors.map((comp: any, idx: number) => {
                                  const isYourBusiness = comp.place_id === selectedLocation?.placeId;
                                  return (
                                    <TableRow key={idx} className={isYourBusiness ? "bg-primary/5" : ""}>
                                      <TableCell className="text-center">
                                        <div className={`mx-auto w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${comp.rank === 1 ? 'bg-green-500 text-white' :
                                          comp.rank === 2 || comp.rank === 3 ? 'bg-green-400 text-white' :
                                            comp.rank < 20 ? 'bg-amber-500 text-white' :
                                              'bg-gray-200 dark:bg-gray-700 text-foreground'
                                          }`}>
                                          {comp.rank}
                                        </div>
                                      </TableCell>
                                      <TableCell className="font-medium flex items-center gap-2">
                                        {comp.locationName || comp.title || 'Unknown'}
                                        {isYourBusiness && <Badge className="bg-primary text-primary-foreground text-[10px]">You</Badge>}
                                      </TableCell>
                                      <TableCell className="text-right">
                                        {comp.rating ? (
                                          <span className="inline-flex items-center justify-end gap-1">
                                            {comp.rating.toFixed(1)} <span className="text-yellow-500 text-xs">★</span>
                                          </span>
                                        ) : '-'}
                                      </TableCell>
                                      <TableCell className="text-right text-muted-foreground">
                                        {comp.reviews?.toLocaleString() || '-'}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* =============== KEYWORDS TAB =============== */}
          <TabsContent tabValue="keywords" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <CardTitle>Tracked Keywords</CardTitle>
                    <CardDescription>Monitor your ranking positions for target keywords</CardDescription>
                  </div>
                  <div className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto">
                    <Input
                      placeholder="Add keyword..."
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e.target.value)}
                      className="w-full sm:w-64"
                      onKeyDown={(e) => e.key === "Enter" && handleAddKeyword()}
                      disabled={isAddingKeyword || !selectedLocationId}
                    />
                    <Button onClick={handleAddKeyword} disabled={isAddingKeyword || !newKeyword.trim() || !selectedLocationId}>
                      {isAddingKeyword ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4 mr-2" />
                      )}
                      Add
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : keywords.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Search className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p>No keywords tracked yet.</p>
                    <p className="text-sm mt-1">Type a keyword above and click Add to start tracking.</p>
                  </div>
                ) : (
                  <>
                    <div className="hidden md:block">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Keyword</TableHead>
                            <TableHead className="text-right">Current Rank</TableHead>
                            <TableHead className="text-right">Change</TableHead>
                            <TableHead className="text-center">Status</TableHead>
                            <TableHead className="text-center">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {keywords.map((kw) => (
                            <TableRow key={kw.keywordId}>
                              <TableCell className="font-medium">{kw.keyword}</TableCell>
                              <TableCell className="text-right">
                                {kw.latestRank === null || kw.latestRank === undefined ? (
                                  <span className="text-muted-foreground">—</span>
                                ) : kw.latestRank >= 101 ? (
                                  <span className="text-muted-foreground">{'>100'}</span>
                                ) : (
                                  <span className={
                                    kw.latestRank <= 3 ? "text-green-600 font-semibold" :
                                      kw.latestRank < 20 ? "text-amber-600 font-semibold" :
                                        "text-red-600"
                                  }>
                                    {kw.latestRank}
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="text-right">{getChangeIcon(kw.change)}</TableCell>
                              <TableCell className="text-center">
                                <Button
                                  variant={kw.isTracked ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => handleToggleKeyword(kw)}
                                >
                                  {kw.isTracked ? "Tracking" : "Paused"}
                                </Button>
                              </TableCell>
                              <TableCell className="text-center">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleOpenSettings(kw)}
                                >
                                  <Settings className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteKeyword(kw)}
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    <div className="space-y-3 md:hidden">
                      {keywords.map((kw) => (
                        <Card key={kw.keywordId} className="p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="font-semibold text-foreground break-words">{kw.keyword}</p>
                              <div className="mt-2 space-y-1 text-sm">
                                <p>
                                  <span className="text-muted-foreground">Current Rank: </span>
                                  {kw.latestRank === null || kw.latestRank === undefined ? (
                                    <span className="text-muted-foreground">—</span>
                                  ) : kw.latestRank >= 101 ? (
                                    <span className="text-muted-foreground">{'>100'}</span>
                                  ) : (
                                    <span className={
                                      kw.latestRank <= 3 ? "text-green-600 font-semibold" :
                                        kw.latestRank < 20 ? "text-amber-600 font-semibold" :
                                          "text-red-600"
                                    }>
                                      {kw.latestRank}
                                    </span>
                                  )}
                                </p>
                                <div className="flex items-center gap-2">
                                  <span className="text-muted-foreground">Change:</span>
                                  {getChangeIcon(kw.change)}
                                </div>
                              </div>
                            </div>
                            <Button
                              variant={kw.isTracked ? "default" : "outline"}
                              size="sm"
                              onClick={() => handleToggleKeyword(kw)}
                            >
                              {kw.isTracked ? "Tracking" : "Paused"}
                            </Button>
                          </div>

                          <div className="mt-3 flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenSettings(kw)}
                            >
                              <Settings className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteKeyword(kw)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* =============== COMPETITORS TAB =============== */}
          <TabsContent tabValue="competitors" className="space-y-6">
            {/* Tracked Competitors Section */}
            <Card>
              <CardHeader>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="h-5 w-5 text-primary" />
                      Tracked Competitors
                    </CardTitle>
                    <CardDescription>
                      Competitors you are manually monitoring against your target keywords
                    </CardDescription>
                  </div>
                  <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center lg:w-auto">
                    <div className="flex w-full items-center gap-2 sm:w-auto">
                      <Label className="shrink-0 text-sm">Keyword:</Label>
                      <Select value={selectedGeoKeywordId} onValueChange={setSelectedGeoKeywordId}>
                        <SelectTrigger className="w-full sm:w-64">
                          <SelectValue placeholder="Select keyword" />
                        </SelectTrigger>
                        <SelectContent>
                          {trackedKeywords.map(kw => (
                            <SelectItem key={kw.keywordId} value={kw.keywordId}>{kw.keyword}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button className="w-full sm:w-auto" onClick={() => setIsAddCompetitorOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Competitor
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {!selectedLocation?.competitors || selectedLocation.competitors.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground border border-dashed rounded-lg">
                    <Target className="h-8 w-8 mx-auto mb-3 opacity-30" />
                    <p>You aren't tracking any competitors directly.</p>
                    <p className="text-sm mt-1">Click "Add Competitor" to benchmark your rankings against specific businesses.</p>
                  </div>
                ) : (
                  <>
                    <div className="hidden md:block overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Competitor</TableHead>
                            <TableHead className="text-right">Avg. Rank</TableHead>
                            <TableHead className="text-right">Appearances</TableHead>
                            <TableHead className="text-center">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {/* Your business baseline row */}
                          <TableRow className="bg-primary/5 border-b-2">
                            <TableCell className="font-medium">
                              {selectedLocation.locationName}
                              <Badge className="ml-2 bg-primary text-primary-foreground">Your Business</Badge>
                            </TableCell>
                            <TableCell className="text-right font-semibold text-green-600">
                              {avgPosition || '-'}
                            </TableCell>
                            <TableCell className="text-right font-medium">{rankedKeywords.length}</TableCell>
                            <TableCell className="text-center">-</TableCell>
                          </TableRow>
                          {selectedLocation.competitors.map((trackedComp) => {
                            // Find if this tracked competitor appeared in the top discoveries
                            const discovered = computedCompetitors.find(c => c.place_id === trackedComp.placeId);
                            return (
                              <TableRow key={trackedComp.placeId}>
                                <TableCell className="font-medium">{trackedComp.locationName}</TableCell>
                                <TableCell className="text-right font-semibold">
                                  {discovered ? discovered.avgRank : '-'}
                                </TableCell>
                                <TableCell className="text-right">
                                  {discovered ? discovered.appearances : '0'}
                                </TableCell>
                                <TableCell className="text-center">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemoveCompetitor(trackedComp.placeId)}
                                    className="text-destructive hover:text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>

                    <div className="space-y-3 md:hidden">
                      <Card className="p-4 bg-primary/5 border-primary/20">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-semibold text-foreground break-words">{selectedLocation.locationName}</p>
                            <Badge className="mt-2 bg-primary text-primary-foreground">Your Business</Badge>
                            <div className="mt-3 space-y-1 text-sm">
                              <p><span className="text-muted-foreground">Avg. Rank: </span><span className="font-semibold text-green-600">{avgPosition || '-'}</span></p>
                              <p><span className="text-muted-foreground">Appearances: </span><span className="font-medium">{rankedKeywords.length}</span></p>
                            </div>
                          </div>
                        </div>
                      </Card>

                      {selectedLocation.competitors.map((trackedComp) => {
                        const discovered = computedCompetitors.find(c => c.place_id === trackedComp.placeId);
                        return (
                          <Card key={trackedComp.placeId} className="p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="font-semibold text-foreground break-words">{trackedComp.locationName}</p>
                                <div className="mt-3 space-y-1 text-sm">
                                  <p><span className="text-muted-foreground">Avg. Rank: </span><span className="font-semibold">{discovered ? discovered.avgRank : '-'}</span></p>
                                  <p><span className="text-muted-foreground">Appearances: </span><span>{discovered ? discovered.appearances : '0'}</span></p>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveCompetitor(trackedComp.placeId)}
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Discovered Competitors Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-amber-500" />
                  Discovered Competitors
                </CardTitle>
                <CardDescription>
                  Businesses automatically discovered ranking in your tracked keyword SERP results
                </CardDescription>
              </CardHeader>
              <CardContent>
                {computedCompetitors.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p>No discovered competitors yet.</p>
                    <p className="text-sm mt-1">Competitors appear after a scan runs and ranks are processed.</p>
                  </div>
                ) : (
                  <>
                    <div className="hidden md:block overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Competitor</TableHead>
                            <TableHead className="text-right">Avg. Rank</TableHead>
                            <TableHead className="text-right">Appearances</TableHead>
                            <TableHead className="text-right">Rating</TableHead>
                            <TableHead className="text-right">Reviews</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {computedCompetitors
                            // Filter out manually tracked ones to avoid duplication in the UI if desired,
                            // or just highlight them. Let's filter out your own business just in case.
                            .filter(c => c.place_id !== selectedLocation?.placeId)
                            .map((comp, idx) => {
                              // Highlight if it's a manually tracked competitor
                              const isTracked = selectedLocation?.competitors?.some(c => c.placeId === comp.place_id);
                              return (
                                <TableRow key={comp.place_id || idx} className={isTracked ? "bg-muted/50" : ""}>
                                  <TableCell className="font-medium">
                                    {comp.title}
                                    {isTracked && <Badge variant="outline" className="ml-2 text-xs">Tracked</Badge>}
                                  </TableCell>
                                  <TableCell className="text-right">{comp.avgRank}</TableCell>
                                  <TableCell className="text-right">{comp.appearances}</TableCell>
                                  <TableCell className="text-right">
                                    {comp.rating ? `${comp.rating.toFixed(1)} ⭐` : '-'}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {comp.reviews?.toLocaleString() || '-'}
                                  </TableCell>
                                </TableRow>
                              )
                            })}
                        </TableBody>
                      </Table>
                    </div>

                    <div className="space-y-3 md:hidden">
                      {computedCompetitors
                        .filter(c => c.place_id !== selectedLocation?.placeId)
                        .map((comp, idx) => {
                          const isTracked = selectedLocation?.competitors?.some(c => c.placeId === comp.place_id);
                          return (
                            <Card key={comp.place_id || idx} className={isTracked ? "p-4 bg-muted/40" : "p-4"}>
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="font-semibold text-foreground break-words">
                                    {comp.title}
                                    {isTracked && <Badge variant="outline" className="ml-2 text-xs">Tracked</Badge>}
                                  </p>
                                  <div className="mt-3 space-y-1 text-sm">
                                    <p><span className="text-muted-foreground">Avg. Rank: </span><span>{comp.avgRank}</span></p>
                                    <p><span className="text-muted-foreground">Appearances: </span><span>{comp.appearances}</span></p>
                                    <p><span className="text-muted-foreground">Rating: </span><span>{comp.rating ? `${comp.rating.toFixed(1)} ⭐` : '-'}</span></p>
                                    <p><span className="text-muted-foreground">Reviews: </span><span>{comp.reviews?.toLocaleString() || '-'}</span></p>
                                  </div>
                                </div>
                              </div>
                            </Card>
                          );
                        })}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* =============== KEYWORD RESEARCH TAB =============== */}
          <TabsContent tabValue="research" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="h-5 w-5 text-primary" />
                  Keyword Research
                </CardTitle>
                <CardDescription>
                  Discover new keyword opportunities for your business
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex gap-3">
                  <Input
                    placeholder="Enter a seed keyword (e.g., plumber, dentist, locksmith)"
                    className="flex-1"
                    value={seedKeyword}
                    onChange={(e) => setSeedKeyword(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleResearch()}
                  />
                  <Button
                    onClick={handleResearch}
                    disabled={isResearching || !seedKeyword.trim()}
                    variant="default"
                    size="default"
                    className=""
                  >
                    {isResearching ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4 mr-2" />
                    )}
                    Research
                  </Button>
                </div>

                {selectedLocation && (
                  <p className="text-sm text-muted-foreground">
                    Searching near: <strong>{selectedLocation.locationName || selectedLocation.name}</strong> ({selectedLocation.lat.toFixed(4)}, {selectedLocation.lng.toFixed(4)})
                  </p>
                )}

                {isResearching && (
                  <div className="text-center py-12">
                    <Loader2 className="h-8 w-8 mx-auto mb-4 animate-spin text-primary" />
                    <p className="text-muted-foreground">Researching keyword suggestions...</p>
                  </div>
                )}

                {!isResearching && suggestedKeywords.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-4">Suggested Keywords ({suggestedKeywords.length})</h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Keyword</TableHead>
                          <TableHead className="text-right">Search Volume</TableHead>
                          <TableHead className="text-right">Difficulty</TableHead>
                          <TableHead>Competition</TableHead>
                          <TableHead>Intent</TableHead>
                          <TableHead className="text-center">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {suggestedKeywords.map((kw, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{kw.keyword}</TableCell>
                            <TableCell className="text-right">{kw.searchVolume?.toLocaleString() || '-'}</TableCell>
                            <TableCell className="text-right">
                              <span className={
                                kw.keywordDifficulty <= 30 ? "text-green-600 font-semibold" :
                                  kw.keywordDifficulty <= 60 ? "text-amber-600 font-semibold" :
                                    "text-red-600 font-semibold"
                              }>
                                {kw.keywordDifficulty}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge className={
                                kw.competition === 'LOW' ? 'bg-green-100 text-green-700 border-green-200' :
                                  kw.competition === 'MEDIUM' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                                    kw.competition === 'HIGH' ? 'bg-red-100 text-red-700 border-red-200' :
                                      'bg-muted text-muted-foreground'
                              }>
                                {kw.competition || 'N/A'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                                {kw.searchIntent || 'unknown'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              {addedSuggestions.has(kw.keyword) ? (
                                <Badge className="bg-green-100 text-green-700 border-green-200">Added ✓</Badge>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className=""
                                  onClick={() => handleAddSuggestedKeyword(kw)}
                                >
                                  <Plus className="h-4 w-4 mr-1" />
                                  Add to Track
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {!isResearching && suggestedKeywords.length === 0 && seedKeyword && (
                  <div className="text-center py-12 text-muted-foreground">
                    <Search className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p>No suggestions found. Try a different seed keyword.</p>
                  </div>
                )}

                {!isResearching && !seedKeyword && (
                  <div className="text-center py-12 text-muted-foreground">
                    <Search className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p className="text-lg">Enter a seed keyword to discover opportunities</p>
                    <p className="text-sm mt-1">Results will be geo-targeted to your selected business location.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>
      </div>


      {/* Settings Dialog */}
      <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Keyword Settings</DialogTitle>
            <DialogDescription>
              Configure the geo-grid scan parameters for "{editingKeyword?.keyword}".
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="gridSize" className="text-right">
                Grid Size
              </Label>
              <Select
                value={editGridSize.toString()}
                onValueChange={(val) => setEditGridSize(parseInt(val, 10))}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select grid size" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3x3 (9 points)</SelectItem>
                  <SelectItem value="5">5x5 (25 points)</SelectItem>
                  <SelectItem value="7">7x7 (49 points)</SelectItem>
                  <SelectItem value="9">9x9 (81 points)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="radiusMiles" className="text-right">
                Radius (miles / mi)
              </Label>
              <div className="col-span-3 flex items-center gap-2">
                <Input
                  id="radiusMiles"
                  type="number"
                  min={0.1}
                  max={50}
                  step={0.1}
                  value={editRadiusMiles}
                  onChange={(e) => setEditRadiusMiles(parseFloat(e.target.value) || kmToMiles(DEFAULT_RADIUS_KM))}
                  className="w-full"
                />
                <span className="text-sm text-muted-foreground w-12">mi</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Note: Changes will apply to the next automatic weekly scan or when you click "Scan Now" in the Geo-Grid tab.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSettingsOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveSettings} disabled={isSavingSettings}>
              {isSavingSettings ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save changes"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Add Competitor Dialog */}
      <Dialog open={isAddCompetitorOpen} onOpenChange={(open) => {
        setIsAddCompetitorOpen(open);
        if (!open) { setCompetitorSearchQuery(""); clearCompetitorPredictions(); setNewCompetitorName(""); setNewCompetitorPlaceId(""); }
      }}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Add Tracked Competitor</DialogTitle>
            <DialogDescription>
              Search for a competitor's business by name. Select from the dropdown to track them.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Search input */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                {competitorSearchLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : (
                  <Search className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
              <Input
                className="pl-9"
                placeholder="Search business name or address..."
                value={competitorSearchQuery}
                onChange={(e: any) => {
                  setCompetitorSearchQuery(e.target.value);
                  // Clear selection if user edits
                  if (newCompetitorPlaceId) { setNewCompetitorPlaceId(""); setNewCompetitorName(""); }
                }}
              />
            </div>

            {/* Predictions dropdown */}
            {competitorPredictions.length > 0 && (
              <div className="border rounded-md bg-background shadow-md overflow-hidden max-h-60 overflow-y-auto">
                {competitorPredictions.map((p: any) => (
                  <div
                    key={p.place_id}
                    className="p-3 hover:bg-muted cursor-pointer border-b last:border-0 flex items-start gap-3 transition-colors"
                    onClick={() => handleCompetitorPredictionSelect(p.place_id, p.description)}
                  >
                    <MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">{p.structured_formatting.main_text}</p>
                      <p className="text-xs text-muted-foreground">{p.structured_formatting.secondary_text}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Selected business confirmation */}
            {newCompetitorPlaceId && (
              <div className="flex items-center gap-2 p-3 bg-primary/10 border border-primary/20 rounded-md">
                <Target className="h-4 w-4 text-primary shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-foreground">{newCompetitorName}</p>
                  <p className="text-xs text-muted-foreground">Place ID: {newCompetitorPlaceId.slice(0, 20)}...</p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddCompetitorOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddCompetitor}
              disabled={isAddingCompetitor || !newCompetitorName.trim() || !newCompetitorPlaceId.trim()}
            >
              {isAddingCompetitor ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                "Add Competitor"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default LocalRankTracker;
